import { motion } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";

const REELS = [
  {
    id: "reel-1",
    title: "Urban Duty Cycle",
    subtitle: "City & Airport Runs",
    poster: "https://images.unsplash.com/photo-1580273916550-e323be2ae537?auto=format&fit=crop&w=1600&q=80",
    src: "https://videos.pexels.com/video-files/2103099/2103099-hd_1920_1080_30fps.mp4",
    accent: "from-red-500/30 via-transparent to-transparent",
  },
  {
    id: "reel-2",
    title: "Highway Reliability",
    subtitle: "Tourist & Outstation Ops",
    poster: "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?auto=format&fit=crop&w=1600&q=80",
    src: "https://videos.pexels.com/video-files/2053100/2053100-uhd_2560_1440_30fps.mp4",
    accent: "from-blue-500/30 via-transparent to-transparent",
  },
  {
    id: "reel-3",
    title: "Night Duty",
    subtitle: "24/7 Airport & Corporate",
    poster: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1600&q=80",
    src: "https://videos.pexels.com/video-files/2278095/2278095-hd_1920_1080_30fps.mp4",
    accent: "from-indigo-500/30 via-transparent to-transparent",
  },
  {
    id: "reel-4",
    title: "Fleet Motion",
    subtitle: "Business at Scale",
    poster: "https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=1600&q=80",
    src: "https://videos.pexels.com/video-files/1409899/1409899-hd_1920_1080_25fps.mp4",
    accent: "from-red-500/30 via-transparent to-transparent",
  },
];

export default function CarVideoShowcase() {
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [active]);

  const activeReel = REELS[active];

  return (
    <section
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
      data-testid="video-showcase-section"
    >
      {/* Ambient glow */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] radial-glow-blue pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[400px] radial-glow-red pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-red-500 mb-4 font-mono-brand"
          >
            <span className="w-8 h-px bg-current" /> In Motion
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]"
          >
            <span className="text-gradient-chrome">Watch Our Vehicles </span>
            <span className="text-gradient-red">Come Alive</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed"
          >
            From city taxi runs to airport and highway duty cycles — cinematic
            reels of the vehicles that will power your commercial business.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main player */}
          <motion.div
            key={activeReel.id}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-8 relative rounded-3xl overflow-hidden border border-white/10 aspect-video bg-slate-950 shadow-[0_40px_100px_rgba(30,64,175,0.25)] group"
            data-testid="video-showcase-main"
          >
            <video
              ref={videoRef}
              key={activeReel.src}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              loop
              muted={muted}
              playsInline
              preload="metadata"
              poster={activeReel.poster}
            >
              <source src={activeReel.src} type="video/mp4" />
            </video>

            {/* Cinematic overlays */}
            <div className={`absolute inset-0 bg-gradient-to-t ${activeReel.accent} pointer-events-none`} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050B24] via-transparent to-transparent pointer-events-none" />

            {/* Frame corners */}
            <span className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-red-500/60" />
            <span className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-blue-400/60" />
            <span className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-blue-400/60" />
            <span className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-red-500/60" />

            {/* Meta pill */}
            <div className="absolute top-6 left-6 flex items-center gap-2 bg-black/50 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shadow-[0_0_10px_#ef4444]" />
              <span className="text-white text-[10px] font-mono-brand tracking-widest uppercase">Live Reel</span>
            </div>

            {/* Mute toggle */}
            <button
              onClick={() => setMuted((m) => !m)}
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/15 flex items-center justify-center text-white hover:bg-red-600 transition-colors"
              aria-label={muted ? "Unmute video" : "Mute video"}
              data-testid="video-mute-toggle"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>

            {/* Title overlay */}
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-red-400 font-mono-brand mb-2">
                  {activeReel.subtitle}
                </div>
                <div className="font-display font-black text-3xl md:text-4xl text-white leading-none tracking-tight">
                  {activeReel.title}
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-1 text-[10px] font-mono-brand text-slate-400 uppercase tracking-widest">
                {String(active + 1).padStart(2, "0")} / {String(REELS.length).padStart(2, "0")}
              </div>
            </div>
          </motion.div>

          {/* Playlist thumbnails */}
          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-3 lg:gap-4">
            {REELS.map((r, i) => (
              <button
                key={r.id}
                onClick={() => setActive(i)}
                className={`group relative rounded-2xl overflow-hidden aspect-video lg:aspect-[16/9] border transition-all duration-300 ${
                  active === i
                    ? "border-red-500 shadow-[0_10px_40px_rgba(220,38,38,0.35)] scale-[1.02]"
                    : "border-white/10 hover:border-white/25"
                }`}
                data-testid={`video-thumb-${i}`}
                aria-label={`Play ${r.title}`}
              >
                <img
                  src={r.poster}
                  alt={r.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                {active !== i && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-11 h-11 rounded-full bg-red-600/90 flex items-center justify-center shadow-[0_8px_24px_rgba(220,38,38,0.5)] group-hover:scale-110 transition-transform">
                      <Play className="w-4 h-4 text-white translate-x-[1px]" fill="white" />
                    </div>
                  </div>
                )}
                <div className="absolute bottom-2 left-3 right-3 text-left">
                  <div className="text-[8px] uppercase tracking-[0.24em] text-red-300 font-mono-brand">
                    {r.subtitle}
                  </div>
                  <div className="font-display font-bold text-white text-xs md:text-sm leading-tight truncate">
                    {r.title}
                  </div>
                </div>
                {active === i && (
                  <span className="absolute top-2 right-2 flex items-center gap-1 bg-red-600 rounded-full px-2 py-0.5">
                    <span className="w-1 h-1 rounded-full bg-white animate-pulse" />
                    <span className="text-white text-[8px] font-mono-brand tracking-widest uppercase">Now</span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
